import {
  IonButton,
  IonChip,
  IonContent,
  IonFooter,
  IonLabel,
  IonText,
  useIonModal,
  useIonRouter,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { checkmarkCircleOutline, alertCircleOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import ChipsGroup from '@components/ChipsGroup';
import Content from '@components/Content';
import { Icon } from '@components/Icon';
import { Spinner } from '@components/Spinner';
import { useCountdown } from '@hooks/useCountdown';
import { CheckAction, CheckEntity } from '@pages/Check/Enums';
import { CheckErrorContent } from '@pages/Check/Error';
import { CheckActionErrorCode as ErrorCode, useCheckActionMutation } from '@pages/Check/queries';
import { formatDateMonthLong, formatTime } from '@services/date-service';
import { getEventInfo, getKioskColor, getPeopleGroupsVisibility } from '@state/event';
import { getKioskEnabled, getKioskName } from '@state/kiosk';
import { getLocale } from '@state/system';

const getErrorMessage = (error: ErrorCode) => {
  const errorMessages: Record<string, string> = {
    [ErrorCode.Invalid]: t`Not a valid UUID.`,
    [ErrorCode.NotFound]: t`No person found for given parameters.`,
    [ErrorCode.NotAuthorized]: t`We are unable to process your request. Please contact an event staff for assistance.`,
    [ErrorCode.CheckOutNotEnabled]: t`Check out is not enabled for this event.`,
    [ErrorCode.Default]: t`Unable to update status`,
  };
  return errorMessages[error] || errorMessages[ErrorCode.Default];
};

const Title = styled.h5`
  font-weight: 500;
`;

const Subtitle = styled.p`
  margin: 8px 0;
`;

const Email = styled(IonText)`
  margin-bottom: 16px;
`;

const Footer = styled(IonFooter)`
  padding: 0 16px 48px;
`;

const CountdownText = styled.p`
  margin-top: 0;
  text-align: center;
`;

const selector = createSelector(
  getLocale,
  getEventInfo,
  getKioskEnabled,
  getKioskColor,
  getKioskName,
  getPeopleGroupsVisibility,
  (locale, event, kioskEnabled, kioskColor, kioskName, showPeopleGroups) => ({
    locale,
    eventId: event.id,
    kioskEnabled,
    kioskColor,
    kioskName,
    showPeopleGroups,
  })
);

interface CheckActionModalProps {
  title: string;
  subtitle?: string;
  entityType: CheckEntity;
  entityId: string;
  action: CheckAction;
  checkinCode: string;
  dismiss: () => void;
}

const CheckActionModal = ({
  title,
  subtitle = undefined,
  entityType,
  entityId,
  action,
  checkinCode,
  dismiss,
}: CheckActionModalProps) => {
  const { locale, eventId, kioskEnabled, kioskColor, kioskName, showPeopleGroups } =
    useSelector(selector);
  const [showCountdown, setShowCountdown] = useState(true);

  const { mutate: createCheckAction, data, isLoading, error } = useCheckActionMutation(eventId);

  // Check in as soon as the component is mounted
  useEffect(() => {
    createCheckAction({
      deviceName: kioskName,
      action,
      person: { checkinCode },
      entityType,
      entityId,
    });
  }, [createCheckAction, checkinCode, kioskName, eventId, action, entityType, entityId]);

  const onCountdownEnd = useCallback(() => {
    if (showCountdown) {
      dismiss();
    }
  }, [showCountdown, dismiss]);
  const countdown = useCountdown(onCountdownEnd, { running: showCountdown && !isLoading });

  if (isLoading) {
    return (
      <IonContent>
        <Content style={{ justifyContent: 'center' }}>
          <Spinner $size="large" />
        </Content>
      </IonContent>
    );
  }

  if (!data || error) {
    const code = error?.errors[0].code || ErrorCode.Default;
    const innerError = getErrorMessage(error?.errors[0].code || ErrorCode.Default);
    const message =
      action === CheckAction.CheckIn
        ? t`An error occurred while checking in: ${innerError}`
        : t`An error occurred while checking out: ${innerError}`;
    let errorContent = (
      <>
        <Icon
          $color={(kioskEnabled && kioskColor) || '--ion-color-danger'}
          icon={alertCircleOutline}
          color="danger"
        />
        <IonText color="danger">{message}</IonText>
      </>
    );
    if (code === ErrorCode.NotFound) {
      errorContent = <CheckErrorContent />;
    }
    return (
      <>
        <IonContent>
          <Content style={{ justifyContent: 'center' }}>{errorContent}</Content>
        </IonContent>
        <Footer>
          <CountdownText>{t`You will be redirected in ${countdown}...`}</CountdownText>
          <IonButton expand="block" fill="solid" onClick={dismiss}>{t`Done`}</IonButton>
        </Footer>
      </>
    );
  }

  const checkAction = action === CheckAction.CheckIn ? t`Check In` : t`Check Out`;
  const { firstName, lastName, email, groups } = data.person;
  const date = formatDateMonthLong({ utcDate: data.checkinAt, locale });
  const time = formatTime({
    utcDate: data.checkinAt,
    locale,
    is24HourFormat: false,
  });

  return (
    <>
      <IonContent>
        <Content style={{ paddingBottom: 16 }}>
          <Icon
            $color={(kioskEnabled && kioskColor) || '--ion-color-primary'}
            icon={checkmarkCircleOutline}
          />
          <h1 color="light">
            {firstName} {lastName}
          </h1>
          <Email color="medium">{email}</Email>
          {showPeopleGroups && groups.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <ChipsGroup
                labels={groups.map(({ name }) => name)}
                // skipcq: JS-0417
                onShowAll={() => setShowCountdown(false)}
              />
            </div>
          )}
          <IonChip color="success">
            <IonLabel color="success">{checkAction}</IonLabel>
          </IonChip>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {date} {time}
        </Content>
      </IonContent>
      <Footer>
        {showCountdown && (
          <CountdownText>{t`You will be redirected in ${countdown}...`}</CountdownText>
        )}
        <IonButton expand="block" fill="solid" onClick={dismiss}>{t`Done`}</IonButton>
      </Footer>
    </>
  );
};

interface UseCheckActionModalArgs extends Omit<CheckActionModalProps, 'dismiss' | 'checkinCode'> {
  goBack?: boolean;
  onDismiss?: () => void;
}

export const useCheckActionModal = ({
  goBack = false,
  onDismiss = undefined,
  ...props
}: UseCheckActionModalArgs) => {
  const router = useIonRouter();
  const [checkinCode, setCheckinCode] = useState<string>();

  const [showRaw, hide] = useIonModal(CheckActionModal, {
    ...props,
    checkinCode,
    dismiss: () => {
      hide();
      onDismiss?.();
      if (goBack) {
        router.goBack();
      }
    },
  });

  const show = useCallback(
    (code: string) => {
      if (code) {
        setCheckinCode(code);
        showRaw({ backdropDismiss: false });
      }
    },
    [showRaw]
  );

  return { show, hide };
};
